import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { sampleNotifications } from '../data/mockData';
import AcumenAICircle from './admin/AcumenAICircle';

function NotificationsCenter({ onOpenChat }) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState(sampleNotifications);
  const [declineReason, setDeclineReason] = useState({});
  const [showDeclineInput, setShowDeclineInput] = useState({});
  const [activeTab, setActiveTab] = useState('notifications'); // 'notifications' or 'messages'
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;
  
  // Simulated messages data
  const [messages, setMessages] = useState([
    { id: 'm1', sender: 'Sarah Johnson', avatar: 'SJ', message: 'Quick question about the T4NG proposal timeline...', time: '10 min ago', read: false },
    { id: 'm2', sender: 'Michael Chen', avatar: 'MC', message: 'Updated the compliance checklist for VISN 8', time: '25 min ago', read: false },
    { id: 'm3', sender: 'Emily Davis', avatar: 'ED', message: 'Can you review the draft before EOD?', time: '1 hour ago', read: false },
    { id: 'm4', sender: 'James Wilson', avatar: 'JW', message: 'Great work on the EHR integration plan!', time: '2 hours ago', read: true },
  ]);
  const unreadMessages = messages.filter(m => !m.read).length;
  
  // Check if all notifications are handled (read + meetings RSVP'd)
  const pendingMeetings = notifications.filter(n => n.type === 'meeting' && n.rsvpStatus === null).length;
  const hasUnhandledItems = unreadCount > 0 || unreadMessages > 0 || pendingMeetings > 0;
  const meetingNotifications = notifications.filter(n => n.type === 'meeting');
  const otherNotifications = notifications.filter(n => n.type !== 'meeting');

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case 'deadline':
        return (
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'compliance':
        return (
          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        );
      case 'opportunity':
        return (
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
        );
      case 'submission':
        return (
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
      case 'team':
        return (
          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        );
      case 'ai':
        return (
          <div className="w-8 h-8 flex items-center justify-center">
            <AcumenAICircle size="sm" isProcessing={false} />
          </div>
        );
      case 'meeting':
        return (
          <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
        );
    }
  };

  const markAsRead = (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllAsRead = () => {
    // Mark all notifications as read AND auto-RSVP yes to all pending meetings
    setNotifications(prev => prev.map(n => ({ 
      ...n, 
      read: true,
      // Auto-accept meetings when marking all as read
      rsvpStatus: n.type === 'meeting' && n.rsvpStatus === null ? 'yes' : n.rsvpStatus
    })));
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    setIsOpen(false);
    navigate(notification.link);
  };

  // Handle RSVP with thumbs up/down
  const handleRSVP = (notificationId, response) => {
    if (response === 'no') {
      setShowDeclineInput(prev => ({ ...prev, [notificationId]: true }));
    } else {
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, rsvpStatus: response, read: true } : n
      ));
    }
  };

  const submitDecline = (notificationId) => {
    setNotifications(prev => prev.map(n => 
      n.id === notificationId ? { ...n, rsvpStatus: 'no', declineReason: declineReason[notificationId] || 'Unable to attend', read: true } : n
    ));
    setShowDeclineInput(prev => ({ ...prev, [notificationId]: false }));
    setDeclineReason(prev => ({ ...prev, [notificationId]: '' }));
  };

  // Total unread for the badge
  const totalUnread = unreadCount + unreadMessages;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Animated Bell Button - Compact size to match reminder+ badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-1.5 rounded-lg transition-all ${
          isOpen 
            ? (darkMode ? 'bg-gray-700 ring-2 ring-blue-500' : 'bg-gray-100 ring-2 ring-blue-500')
            : (darkMode ? 'bg-gray-750 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100')
        } ${darkMode ? 'border border-gray-600' : 'border border-gray-200'}`}
      >
        {/* Bell Icon - Smaller size for consistency */}
        <div className="relative">
          <svg 
            className={`w-4.5 h-4.5 transition-all ${
              hasUnhandledItems 
                ? 'text-amber-500'
                : (darkMode ? 'text-gray-400' : 'text-gray-400')
            }`}
            style={{ 
              width: '18px', 
              height: '18px',
              ...(hasUnhandledItems ? {
                animation: 'bellRing 4s ease-in-out infinite',
                transformOrigin: 'top center'
              } : {})
            }}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>
      </button>

      {/* CSS for bell ring animation */}
      <style>{`
        @keyframes bellRing {
          0%, 100% { transform: rotate(0deg); }
          5% { transform: rotate(12deg); }
          10% { transform: rotate(-12deg); }
          15% { transform: rotate(8deg); }
          20% { transform: rotate(-8deg); }
          25% { transform: rotate(4deg); }
          30% { transform: rotate(0deg); }
        }
      `}</style>

      {isOpen && (
        <div className={`absolute right-0 mt-2 w-[420px] rounded-xl shadow-2xl z-[100] overflow-hidden ${
          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          {/* Header with Tabs */}
          <div className={`px-4 py-3 ${
            darkMode ? 'bg-gray-750 border-b border-gray-700' : 'bg-gray-50 border-b border-gray-100'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Inbox</h3>
              <button
                onClick={() => setIsOpen(false)}
                className={`p-1 rounded-lg transition-colors ${
                  darkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                }`}
                title="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Tab Switcher - Only Alerts and Messages */}
            <div className="flex gap-1 p-1 rounded-lg bg-gray-200/50 dark:bg-gray-700/50">
              <button
                onClick={() => setActiveTab('notifications')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  activeTab === 'notifications'
                    ? (darkMode ? 'bg-gray-600 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm')
                    : (darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900')
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                Alerts
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 text-[10px] bg-red-500 text-white rounded-full">{unreadCount}</span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('messages')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  activeTab === 'messages'
                    ? (darkMode ? 'bg-gray-600 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm')
                    : (darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900')
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Messages
                {unreadMessages > 0 && (
                  <span className="px-1.5 py-0.5 text-[10px] bg-blue-500 text-white rounded-full">{unreadMessages}</span>
                )}
              </button>
            </div>
          </div>

          {/* Notifications Tab Content */}
          {activeTab === 'notifications' && (
            <>
              {/* Action bar */}
              <div className={`px-4 py-2 flex items-center justify-between ${darkMode ? 'bg-gray-750/50' : 'bg-gray-50/50'}`}>
                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {unreadCount} unread
                </span>
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-500 hover:text-blue-600 font-medium"
                >
                  Mark all read
                </button>
              </div>

              {/* Meetings Section - Highlighted at Top */}
              {meetingNotifications.length > 0 && (
                <div className={`${darkMode ? 'bg-teal-900/20 border-b border-teal-800/30' : 'bg-teal-50 border-b border-teal-100'}`}>
                  <div className={`px-4 py-2 flex items-center gap-2 ${darkMode ? 'border-b border-teal-800/30' : 'border-b border-teal-100'}`}>
                    <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className={`text-sm font-semibold ${darkMode ? 'text-teal-400' : 'text-teal-700'}`}>
                      Upcoming Meetings
                    </span>
                    {pendingMeetings > 0 && (
                      <span className="px-1.5 py-0.5 text-xs bg-teal-500 text-white rounded-full">{pendingMeetings}</span>
                    )}
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {meetingNotifications.map(meeting => (
                      <div 
                        key={meeting.id}
                        className={`px-4 py-3 ${darkMode ? 'hover:bg-teal-900/30' : 'hover:bg-teal-100/50'} transition-colors`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {meeting.title}
                            </p>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-0.5`}>
                              {meeting.message}
                            </p>
                            <p className={`text-xs ${darkMode ? 'text-teal-400' : 'text-teal-600'} mt-1`}>
                              {meeting.time}
                            </p>
                            
                            {/* RSVP Section */}
                            {meeting.rsvpStatus === null ? (
                              <div className="mt-2">
                                {showDeclineInput[meeting.id] ? (
                                  <div className="flex flex-col gap-2">
                                    <input
                                      type="text"
                                      placeholder="Reason for declining..."
                                      value={declineReason[meeting.id] || ''}
                                      onChange={(e) => setDeclineReason(prev => ({ ...prev, [meeting.id]: e.target.value }))}
                                      className={`text-xs px-2 py-1.5 rounded-lg border w-full ${
                                        darkMode 
                                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                                      }`}
                                      autoFocus
                                    />
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => submitDecline(meeting.id)}
                                        className="text-xs px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                      >
                                        Submit
                                      </button>
                                      <button
                                        onClick={() => setShowDeclineInput(prev => ({ ...prev, [meeting.id]: false }))}
                                        className={`text-xs px-2 py-1 rounded-lg transition-colors ${
                                          darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Attending?</span>
                                    <button
                                      onClick={() => handleRSVP(meeting.id, 'yes')}
                                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs transition-colors"
                                      title="Yes, I'll attend"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                                      </svg>
                                      Yes
                                    </button>
                                    <button
                                      onClick={() => handleRSVP(meeting.id, 'no')}
                                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs transition-colors"
                                      title="No, I can't attend"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.105-1.79l-.05-.025A4 4 0 0011.055 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
                                      </svg>
                                      No
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className={`mt-2 flex items-center gap-1 text-xs ${
                                meeting.rsvpStatus === 'yes' ? 'text-green-600' : 'text-red-500'
                              }`}>
                                {meeting.rsvpStatus === 'yes' ? (
                                  <>
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span>You&apos;re attending</span>
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    <span>Declined{meeting.declineReason ? `: ${meeting.declineReason}` : ''}</span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Other Notifications */}
              <div className={`${darkMode ? 'border-b border-gray-700' : 'border-b border-gray-100'}`}>
                <div className={`px-4 py-2 ${darkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                  <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Other Notifications
                  </span>
                </div>
              </div>

              {/* Notifications list - excluding meetings */}
              <div className="max-h-64 overflow-y-auto">
                {otherNotifications.length === 0 ? (
                  <div className={`px-4 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <p className="text-sm">No other notifications</p>
                  </div>
                ) : (
                  otherNotifications.map(notification => (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`px-4 py-3 flex items-start gap-3 cursor-pointer transition-colors ${
                        !notification.read 
                          ? darkMode ? 'bg-blue-900/20' : 'bg-blue-50/50'
                          : ''
                      } ${
                        darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      } ${
                        darkMode ? 'border-b border-gray-700' : 'border-b border-gray-100'
                      }`}
                    >
                      {getIcon(notification.type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium truncate ${
                            darkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {notification.title}
                          </p>
                          {!notification.read && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                          )}
                        </div>
                        <p className={`text-xs mt-0.5 line-clamp-2 ${
                          darkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {notification.message}
                        </p>
                        <p className={`text-xs mt-1 ${
                          darkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          {notification.time}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {/* Messages Tab Content */}
          {activeTab === 'messages' && (
            <>
              {/* Action bar for Messages */}
              <div className={`px-4 py-2 flex items-center justify-between ${darkMode ? 'bg-gray-750/50' : 'bg-gray-50/50'}`}>
                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {unreadMessages} unread
                </span>
                <button
                  onClick={() => setMessages(prev => prev.map(m => ({ ...m, read: true })))}
                  className="text-xs text-blue-500 hover:text-blue-600 font-medium"
                >
                  Mark all read
                </button>
              </div>
              
              {/* Messages List */}
              <div className="max-h-72 overflow-y-auto">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    onClick={() => {
                      // Mark message as read
                      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, read: true } : m));
                      setIsOpen(false);
                      if (onOpenChat) onOpenChat();
                    }}
                    className={`px-4 py-3 flex items-start gap-3 cursor-pointer transition-colors ${
                      !msg.read 
                        ? darkMode ? 'bg-blue-900/20' : 'bg-blue-50/50'
                        : ''
                    } ${
                      darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    } ${
                      darkMode ? 'border-b border-gray-700' : 'border-b border-gray-100'
                    }`}
                  >
                    {/* Avatar */}
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                      {msg.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {msg.sender}
                        </p>
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {msg.time}
                        </span>
                      </div>
                      <p className={`text-xs mt-0.5 line-clamp-2 ${
                        !msg.read 
                          ? (darkMode ? 'text-gray-200' : 'text-gray-700')
                          : (darkMode ? 'text-gray-400' : 'text-gray-500')
                      }`}>
                        {msg.message}
                      </p>
                      {!msg.read && (
                        <div className="mt-1">
                          <span className="w-2 h-2 bg-blue-500 rounded-full inline-block"></span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Open Full Chat Button */}
              <div className={`px-4 py-3 ${darkMode ? 'bg-gray-750 border-t border-gray-700' : 'bg-gray-50 border-t border-gray-100'}`}>
                <button 
                  onClick={() => {
                    setIsOpen(false);
                    if (onOpenChat) onOpenChat();
                  }}
                  className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Open Team Chat
                </button>
              </div>
            </>
          )}

          {/* Footer - Only show for notifications tab */}
          {activeTab === 'notifications' && (
            <div className={`px-4 py-3 text-center ${
              darkMode ? 'bg-gray-750 border-t border-gray-700' : 'bg-gray-50 border-t border-gray-100'
            }`}>
              <button 
                onClick={() => { setIsOpen(false); navigate('/settings?section=notifications'); }}
                className="text-sm text-blue-500 hover:text-blue-600 font-medium"
              >
                Notification Settings
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationsCenter;